// Frontend cache for backend data (Cloud Function calls and Firestore reads).
//
// Entries live in localStorage (so they survive reloads) keyed per user, with
// a TTL per data type, plus an in-flight map to dedupe concurrent requests
// within a page session. Mutations (CSV import, adding a stock) must call
// invalidateUserCache() so the next load refetches.
//
// This module is dependency-free: firebase-config registers the signed-in
// user via setCacheUser() on auth state changes.

const PREFIX = "appCache:";
const inflight = new Map(); // full key → Promise, dedupes concurrent fetches

let currentUid = null;

export const TTL = {
  PORTFOLIO: 10 * 60 * 1000,      // holdings list
  TRANSACTIONS: 10 * 60 * 1000,   // imported transaction history
  PRICES: 15 * 60 * 1000,         // quotes (server cache is 12h)
  DIVIDENDS: 6 * 60 * 60 * 1000,  // dividend projections (server cache is 24h)
  HISTORY: 6 * 60 * 60 * 1000,    // price history series
};

export function setCacheUser(uid) {
  if (uid !== currentUid) inflight.clear();
  currentUid = uid || null;
}

function userKey(key) {
  return `${PREFIX}${currentUid || "anon"}:${key}`;
}

export function cacheGet(key) {
  try {
    const raw = window.localStorage.getItem(userKey(key));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || typeof entry.cachedAt !== "number") return null;
    if (Date.now() - entry.cachedAt > entry.ttl) return null;
    return entry.data;
  } catch (_) {
    return null;
  }
}

export function cacheSet(key, data, ttl) {
  try {
    window.localStorage.setItem(
      userKey(key),
      JSON.stringify({ data, ttl, cachedAt: Date.now() })
    );
  } catch (_) { /* storage full/unavailable — caching is best-effort */ }
}

// Returns cached data when fresh; otherwise runs `fetcher`, caches its result
// and returns it. Concurrent calls for the same key share one fetch.
// Pass `shouldCache` to skip persisting incomplete results (they are still
// returned, just not cached).
export async function cachedFetch(key, ttl, fetcher, shouldCache = () => true) {
  const cached = cacheGet(key);
  if (cached !== null) return cached;

  const fullKey = userKey(key);
  if (inflight.has(fullKey)) return inflight.get(fullKey);

  const promise = (async () => {
    try {
      const data = await fetcher();
      if (shouldCache(data)) cacheSet(key, data, ttl);
      return data;
    } finally {
      inflight.delete(fullKey);
    }
  })();
  inflight.set(fullKey, promise);
  return promise;
}

function removeKeysWithPrefix(prefix) {
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => window.localStorage.removeItem(k));
    inflight.clear();
  } catch (_) { /* storage unavailable */ }
}

// Drops every cached entry for the current user. Call after any mutation
// that changes holdings or transactions (CSV import, adding a stock).
export function invalidateUserCache() {
  removeKeysWithPrefix(userKey(""));
}

// Drops cached entries for every user. Called on sign-out so holdings and
// transaction history don't stay in localStorage on a shared device.
export function clearCache() {
  removeKeysWithPrefix(PREFIX);
}
